import React, { useMemo } from 'react';
import { ConversationUpdateModal } from '../../conversation-update-modal/conversation-update-modal';
import { ModalContentsList } from '../../modal-contents-list';
import {
  ConversationAssignee,
  ConversationData,
  dispatch,
  updateSearchKey,
} from '@zonofi/zono-money-store';
import { ModalContentItemData } from '../../../types';
import { Avatar, EmptyStateView, SearchBar } from '../../../components';
import './assign-modal.scss';
import { ReactComponent as UserFilled } from '@zonofi/svg-icons/user-filled.svg';
import { useAssignTeamMember } from '../../hooks/assign-team-member';
import { ToastMessageProps } from '@zonofi/zo-components/toast/zo-toast';
import { SVGS } from '../../../assets';

interface AssignModalProps {
  open: boolean;
  onClose: () => void;
  customerName: string;
  conversationId: string;
  assignedTeamMembers: ConversationAssignee[];
  updateConversations: (item: ConversationData) => void;
  toast?: ToastMessageProps;
  setToast?: React.Dispatch<React.SetStateAction<ToastMessageProps>>;
}

export const AssignModal: React.FC<AssignModalProps> = ({
  open,
  onClose,
  customerName,
  conversationId,
  assignedTeamMembers,
  updateConversations,
}) => {
  const { teamMemberData, onUpdateAssignee, onRemoveAssignee } =
    useAssignTeamMember({
      assigned: assignedTeamMembers,
      open,
      updateConversations,
    });

  const onCloseModal = () => {
    dispatch(updateSearchKey(''));
    onClose();
  };

  const assignedData: ModalContentItemData[] = useMemo(
    () =>
      assignedTeamMembers.length > 0
        ? assignedTeamMembers.map(({ teamMemberId, teamMemberName }) => ({
            title: teamMemberName,
            icon: <Avatar id={teamMemberId} title={teamMemberName} />,
            onPress: () => {
              onRemoveAssignee({ conversationId, teamMemberId });
              onCloseModal();
            },
          }))
        : [
            {
              title: 'None',
              onPress: () => {},
              icon: (
                <div className="assigned-none">
                  <UserFilled color="#8C8C8C" />
                </div>
              ),
            },
          ],
    [assignedTeamMembers]
  );

  const teamMembers: ModalContentItemData[] = useMemo(
    () =>
      teamMemberData.map(({ id, name, mobile, userType, teamMemberRole }) => ({
        title: name,
        icon: <Avatar id={id} title={name} />,
        description: `+${mobile} • ${
          userType === null
            ? 'Team Owner'
            : userType === 'M'
              ? 'Member'
              : userType
        } • ${teamMemberRole?.[0]?.title}`,
        onPress: () => {
          onUpdateAssignee({ conversationId, teamMemberId: id });
          onCloseModal();
        },
      })),
    [teamMemberData, conversationId]
  );

  return (
    <ConversationUpdateModal
      open={open}
      onClose={onCloseModal}
      header="Assign Team Member"
      customerName={customerName}
      style={{ height: '90dvh' }}
    >
      <div className="assign-modal-search-container">
        <SearchBar
          placeholderText={'Search Team Member'}
          className="assign-modal-search-bar"
        />
      </div>
      <div className="assign-modal-section">
        <div className="assign-modal-section-title font-semibold">ASSIGNED</div>
        <ModalContentsList
          data={assignedData}
          showSelection={false}
          endIcon={
            assignedTeamMembers.length > 0 ? (
              <img src={SVGS.Remove} alt="" />
            ) : undefined
          }
        />
      </div>
      <div className="assign-modal-section">
        <div className="assign-modal-section-title font-semibold">
          SUGGESTED
        </div>
        <ModalContentsList
          data={teamMembers}
          showSelection={false}
          emptyView={
            <EmptyStateView
              message={'No team members found!!'}
              className="assign-modal-empty-view"
            />
          }
        />
      </div>
    </ConversationUpdateModal>
  );
};
